/**
 * Bucket query hooks (PHASE1 §7.4) — read-only; mutations live in FE-3.
 *
 * All bucket data flows through the single `['buckets']` cache, which holds the
 * full group→bucket tree (archived included) fetched from `/api/bucket-groups`.
 * The derived views (flat canonical list, id→bucket map, transfer bucket) are
 * memoised off that one cache so `1–9` inbox ordering is deterministic.
 */
import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type { BucketGroupWithBuckets, BucketOut } from '../types.ts';

const STALE = 5 * 60 * 1000;

/**
 * Canonical order: group.sort_order,id then bucket.sort_order,id.
 *
 * `includeArchived` gates BOTH a bucket's own `archived` flag and its parent
 * group's `archived` flag — a bucket in an archived group is excluded just
 * like an archived bucket itself (F054 residue: archiving a group must hide
 * its buckets everywhere, not just the ones flagged individually).
 */
function flatten(tree: BucketGroupWithBuckets[], includeArchived: boolean): BucketOut[] {
  const groups = [...tree].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const out: BucketOut[] = [];
  for (const group of groups) {
    if (!includeArchived && group.archived) continue;
    const buckets = [...group.buckets]
      .filter((b) => includeArchived || !b.archived)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    out.push(...buckets);
  }
  return out;
}

/** Raw `['buckets']` query — the group→bucket tree (archived included). */
export function useBucketTreeQuery(): UseQueryResult<BucketGroupWithBuckets[]> {
  return useQuery({
    queryKey: qk.buckets,
    queryFn: () => api.get<BucketGroupWithBuckets[]>('/api/bucket-groups?include_archived=true'),
    staleTime: STALE,
  });
}

/** Groups with nested buckets; archived hidden unless `includeArchived`. */
export function useBucketTree(includeArchived = false): BucketGroupWithBuckets[] {
  const { data } = useBucketTreeQuery();
  return useMemo(() => {
    const tree = data ?? [];
    const groups = [...tree].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    return groups
      .filter((g) => includeArchived || !g.archived)
      .map((g) => ({
        ...g,
        buckets: [...g.buckets]
          .filter((b) => includeArchived || !b.archived)
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
      }));
  }, [data, includeArchived]);
}

/** Flat buckets in canonical order; archived excluded unless `includeArchived`. */
export function useBuckets(includeArchived = false): BucketOut[] {
  const { data } = useBucketTreeQuery();
  return useMemo(() => flatten(data ?? [], includeArchived), [data, includeArchived]);
}

/** id→bucket map over ALL buckets (archived included, for lookups on old rows). */
export function useBucketMap(): Map<number, BucketOut> {
  const { data } = useBucketTreeQuery();
  return useMemo(() => {
    const map = new Map<number, BucketOut>();
    for (const group of data ?? []) {
      for (const bucket of group.buckets) map.set(bucket.id, bucket);
    }
    return map;
  }, [data]);
}

